import { useEffect, useRef } from 'react'
import useElementDimensions from 'src/customHooks/customHooks'
import { BOOKING_STATUSES, BOOKING_PAYMENT_STATUSES } from 'src/consumer/constants'
import { getMeetingSrcIcon, getMeetingSrcLabel } from 'src/consumer/helpers/meetingSrcUtils'

const BookedSlotHeader = ({
  bookedSlot,
  apptCalendarSettings,
  styles,
  forMentor,
  hidePreviousTherapyReportsButton,
}) => {
  const leftStripRef = useRef(null)
  const leftStripTextRef = useRef(null)

  const {
    dimensions: leftStripDimensions,
    refresh: refreshLeftStripDim,
  } = useElementDimensions({ updateOnScroll: false })

  const {
    dimensions: leftStripTextDimensions,
    refresh: refreshLeftStripTextDim,
  } = useElementDimensions({ updateOnScroll: false })

  const isBookedSlotBlockedForPayment =
    bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
    bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING

  // Determine if appointment number should be shown vertically
  let isShowingVerticalApptNumber = true
  if (leftStripDimensions?.height <= leftStripTextDimensions?.height) {
    isShowingVerticalApptNumber = false
  }

  if (apptCalendarSettings?.showAppointmentNumberHorizontally) {
    isShowingVerticalApptNumber = false
  }

  useEffect(() => {
    refreshLeftStripDim()
    refreshLeftStripTextDim()
  }, [bookedSlot, refreshLeftStripDim, refreshLeftStripTextDim])

  const stripTextColor = styles?.leftStripTextColor || '#FFFFFF'

  const textStyle = {
    transform: isShowingVerticalApptNumber ? 'rotate(180deg)' : '',
    writingMode: isShowingVerticalApptNumber ? 'vertical-lr' : 'unset',
    color: stripTextColor,
    fontWeight: 700,
    fontSize: '13px',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
  }

  const headerStyle = {
    ...styles.leftStripStyle,
    marginRight:
      forMentor && !hidePreviousTherapyReportsButton && bookedSlot?.therapyPatient ? '0px' : '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
  }

  // For BLOCKED text, also check if height is too small (less than 50px) to show horizontally
  const shouldShowBlockedHorizontal = leftStripDimensions?.height < 50

  // Always show the meetingSrc icon at the top of the strip so blocked / cancelled /
  // booked slots all carry a visual mode indicator (Sofa = In Person, Video = Video
  // Call, Home = Home Visit, ? = Mode unknown). The text below remains BLOCKED for
  // payment-pending slots and the appointment number otherwise.
  const meetingSrcIcon = (
    <span
      title={getMeetingSrcLabel(bookedSlot?.meetingSrc)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: '4px',
        filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))',
      }}
    >
      {getMeetingSrcIcon(bookedSlot?.meetingSrc, { size: 16, color: stripTextColor })}
    </span>
  )

  return (
    <div ref={leftStripRef} style={headerStyle}>
      {meetingSrcIcon}
      {isBookedSlotBlockedForPayment ? (
        <span
          ref={leftStripTextRef}
          style={{
            ...textStyle,
            transform: shouldShowBlockedHorizontal ? 'none' : 'rotate(180deg)',
            writingMode: shouldShowBlockedHorizontal ? 'unset' : 'vertical-lr',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '1px',
            color: '#ffffff',
            textTransform: 'uppercase',
            padding: shouldShowBlockedHorizontal ? '4px 6px' : '4px 2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.7), 0 0 2px rgba(0, 0, 0, 0.5)',
          }}
        >
          BLOCKED
        </span>
      ) : (
        <span ref={leftStripTextRef} style={textStyle}>
          {bookedSlot?.appointmentNumber}
        </span>
      )}
    </div>
  )
}

export default BookedSlotHeader
